import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CaretLeftIcon } from "@phosphor-icons/react/dist/csr/CaretLeft";
import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { Button } from "@volca/ui/components/ui/button";
import { ButtonGroup } from "@volca/ui/components/ui/button-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import type { OverviewPeriod } from "@/profitability/overview/types";
import { formatReportPeriod } from "@/profitability/utils";

const label = (period: OverviewPeriod) =>
  formatReportPeriod({
    start_date: period.start_date,
    end_date: period.end_date,
  });

const PeriodStatus = ({ period }: { period: OverviewPeriod }) => {
  if (period.reviewed_at)
    return (
      <span className="flex items-center gap-1 text-success">
        <CheckIcon aria-hidden className="size-3.5" />
        Reviewed
      </span>
    );
  // What that period flagged that is still open; the Review tab's count is
  // everything still open, earlier periods included.
  if (period.active_findings)
    return (
      <span className="text-warning tabular-nums">
        {period.active_findings} flagged
      </span>
    );
  return null;
};

/** Every period, newest first, and the sheet of reruns. */
const PeriodMenu = ({
  periods,
  current,
  onChange,
  onAllPeriods,
  align,
}: {
  periods: OverviewPeriod[];
  current: string;
  onChange: (runId: string | null) => void;
  onAllPeriods: () => void;
  align: "start" | "end";
}) => (
  <DropdownMenuContent
    align={align}
    className="max-h-96 w-auto max-w-[calc(100vw-1rem)] min-w-72 pointer-coarse:[&_[role=menuitem]]:min-h-11"
  >
    {periods.map((period, position) => (
      <DropdownMenuItem
        key={period.run_id}
        aria-current={period.run_id === current ? "true" : undefined}
        onClick={() => onChange(position === 0 ? null : period.run_id)}
        className={cn("justify-between gap-6 whitespace-nowrap", {
          "bg-surface-sunken": period.run_id === current,
        })}
      >
        <Stack direction="row" align="center" gap={1.5}>
          <span className="tabular-nums">{label(period)}</span>
          {position === 0 && <span className="text-subtle">Latest</span>}
        </Stack>
        <PeriodStatus period={period} />
      </DropdownMenuItem>
    ))}
    {periods.length > 0 && <DropdownMenuSeparator />}
    <DropdownMenuItem onClick={onAllPeriods}>
      All periods and reruns
    </DropdownMenuItem>
  </DropdownMenuContent>
);

/**
 * The page's period, beside its title, the way Payroll moves between pay
 * periods: the one before and after by arrow, and every period from the menu,
 * newest first, with where each stands (what it flagged, reviewed). Its foot opens every period with its reruns.
 * `button` is the one control for a narrow column: Change period, same menu.
 */
export const PeriodPicker = ({
  periods,
  current,
  onChange,
  onAllPeriods,
  variant = "group",
  triggerLabel,
}: {
  periods: OverviewPeriod[];
  /** The run of the period on screen. */
  current: string;
  /** Null goes back to the latest period. */
  onChange: (runId: string | null) => void;
  onAllPeriods: () => void;
  /** The grouped control, or one button that opens the same menu. */
  variant?: "group" | "button";
  triggerLabel?: string;
}) => {
  const index = periods.findIndex((period) => period.run_id === current);
  // Newest first: the one after is at index - 1, the one before at + 1.
  const newer = index > 0 ? periods[index - 1] : undefined;
  const older = index >= 0 ? periods[index + 1] : undefined;
  // The latest period is the page's default, so going to it clears the choice.
  const go = (period: OverviewPeriod | undefined) =>
    period && onChange(period === periods[0] ? null : period.run_id);
  if (variant === "button")
    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="sm"
              className="max-w-full min-w-0"
              aria-label={
                triggerLabel
                  ? `Change period, ${triggerLabel}`
                  : "Change period"
              }
            />
          }
        >
          <span className="truncate">{triggerLabel ?? "Change period"}</span>
          <CaretDownIcon aria-hidden />
        </DropdownMenuTrigger>
        <PeriodMenu
          periods={periods}
          current={current}
          onChange={onChange}
          onAllPeriods={onAllPeriods}
          align="end"
        />
      </DropdownMenu>
    );
  return (
    <ButtonGroup aria-label="Period">
      <Button
        variant="outline"
        size="icon-sm"
        aria-label={
          older ? `Previous period, ${label(older)}` : "No earlier period"
        }
        disabled={!older}
        onClick={() => go(older)}
      >
        <CaretLeftIcon aria-hidden />
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger render={<Button variant="outline" size="sm" />}>
          All periods
          <CaretDownIcon aria-hidden data-icon="inline-end" />
        </DropdownMenuTrigger>
        <PeriodMenu
          periods={periods}
          current={current}
          onChange={onChange}
          onAllPeriods={onAllPeriods}
          align="start"
        />
      </DropdownMenu>
      <Button
        variant="outline"
        size="icon-sm"
        aria-label={newer ? `Next period, ${label(newer)}` : "No later period"}
        disabled={!newer}
        onClick={() => go(newer)}
      >
        <CaretRightIcon aria-hidden />
      </Button>
    </ButtonGroup>
  );
};
